import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const ROLE_LABELS = {
  health: { fr: 'Santé suivie', en: 'Tracked health' },
  defense: { fr: 'Défense', en: 'Defense' },
  initiative: { fr: 'Initiative', en: 'Initiative' },
  movement: { fr: 'Déplacement', en: 'Movement' },
  rating: { fr: 'Niveau / danger', en: 'Rating / danger' },
  reward: { fr: 'Récompense', en: 'Reward' },
  custom: { fr: 'Information libre', en: 'Custom information' },
};

export function CreatureStatsEditor({ fields, onChange, language, editable = true, systemName = '' }) {
  const updateField = (index, patch) => {
    onChange(fields.map((field, fieldIndex) => fieldIndex === index ? { ...field, ...patch } : field));
  };

  const updateRole = (index, role) => {
    onChange(fields.map((field, fieldIndex) => {
      if (fieldIndex === index) return { ...field, role };
      if (role !== 'custom' && field.role === role) return { ...field, role: 'custom' };
      return field;
    }));
  };

  const addField = () => {
    onChange([
      ...fields,
      { id: `custom-${Date.now()}`, label: '', value: '', role: 'custom' },
    ]);
  };

  const removeField = (index) => onChange(fields.filter((_, fieldIndex) => fieldIndex !== index));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="font-medium">
            {language === 'fr' ? 'Statistiques du système' : 'System statistics'}
          </Label>
          <p className="text-xs text-muted-foreground">
            {systemName
              ? `${language === 'fr' ? 'Système' : 'System'} : ${systemName}`
              : (language === 'fr' ? 'Champs libres, adaptables à votre jeu' : 'Flexible fields for your game')}
          </p>
        </div>
        {editable && (
          <Button type="button" variant="outline" size="sm" onClick={addField}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            {language === 'fr' ? 'Champ' : 'Field'}
          </Button>
        )}
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-muted-foreground rounded-md border border-dashed p-3 text-center">
          {language === 'fr' ? 'Aucune statistique configurée' : 'No statistics configured'}
        </p>
      ) : (
        <div className="space-y-2">
          {fields.map((field, index) => (
            <div key={field.id || index} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto] gap-2 items-end">
              <div>
                <Label className="text-xs">{language === 'fr' ? 'Nom' : 'Name'}</Label>
                {editable ? (
                  <Input value={field.label || ''} onChange={(e) => updateField(index, { label: e.target.value })} />
                ) : (
                  <p className="h-9 flex items-center text-sm font-medium">{field.label || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-xs">{language === 'fr' ? 'Valeur' : 'Value'}</Label>
                {editable ? (
                  <Input value={field.value ?? ''} onChange={(e) => updateField(index, { value: e.target.value })} />
                ) : (
                  <p className="h-9 flex items-center text-sm">{String(field.value ?? '-')}</p>
                )}
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs">{language === 'fr' ? 'Usage dans le combat' : 'Combat usage'}</Label>
                {editable ? (
                  <Select value={field.role || 'custom'} onValueChange={(role) => updateRole(index, role)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABELS).map(([role, labels]) => (
                        <SelectItem key={role} value={role}>{labels[language] || labels.fr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="h-9 flex items-center text-xs text-muted-foreground">
                    {ROLE_LABELS[field.role]?.[language] || ROLE_LABELS.custom[language]}
                  </p>
                )}
              </div>
              {editable && (
                <Button type="button" variant="ghost" size="icon" className="text-destructive" onClick={() => removeField(index)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
